/**
 * EnvironmentToggle Component
 *
 * Toggle switch for switching between Sandbox and Production environments.
 * Triggers ProductionWarning modal when switching to Production.
 */
import React from 'react';
import { ToggleButtonGroup, ToggleButton, Box, Typography } from '@mui/material';
import { Storage as SandboxIcon, Warning as ProductionIcon } from '@mui/icons-material';
import { useEnvironment } from '../../../hooks/useEnvironment';

export const EnvironmentToggle: React.FC = () => {
  const { environment, toggleEnvironment } = useEnvironment();

  const handleChange = (
    event: React.MouseEvent<HTMLElement>,
    newEnvironment: string | null,
  ) => {
    if (newEnvironment && newEnvironment !== environment) {
      toggleEnvironment();
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
        Environment:
      </Typography>
      <ToggleButtonGroup
        value={environment}
        exclusive
        onChange={handleChange}
        size="small"
        aria-label="environment selector"
      >
        <ToggleButton
          value="sandbox"
          aria-label="sandbox environment"
          sx={{
            '&.Mui-selected': {
              backgroundColor: '#10b981',
              color: 'white',
              '&:hover': {
                backgroundColor: '#059669',
              }
            }
          }}
        >
          <SandboxIcon sx={{ mr: 0.5, fontSize: 18 }} />
          Sandbox
        </ToggleButton>
        <ToggleButton
          value="production"
          aria-label="production environment"
          sx={{
            '&.Mui-selected': {
              backgroundColor: '#ef4444',
              color: 'white',
              '&:hover': {
                backgroundColor: '#dc2626',
              }
            }
          }}
        >
          <ProductionIcon sx={{ mr: 0.5, fontSize: 18 }} />
          Production
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );
};

export default EnvironmentToggle;
